import { useMemo } from "react";
import Input from "../../../../componentes/input/input";
import Button from "../../../../componentes/button/button";
import InputMoney from "../../../../componentes/input/inputMoney";
import Textarea from "../../../../componentes/input/textarea";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import ProdutoEditor from "./ProdutoEditor";
import { FornecedorCotacaoEditor, validarCotacao } from "./CotacaoCamposEditor";
import RateioOrcamentarioEditor from "../shared/RateioOrcamentarioEditor";

/**
 * Campos da Etapa 1 de Solicitação de Compras — reaproveitados tanto na criação
 * (NovaSolicitacaoComprasForm.jsx, dentro do NovaSolicitacaoLayout) quanto na edição de uma
 * solicitação retificada de volta para a Etapa 1 (EtapaCompraSolicitacaoRetificada.jsx, direto no
 * card de "Etapa atual"), para não duplicar UI/validação entre os dois contextos.
 */

export const PRIORIDADE_OPTIONS = [
    { value: "BAIXA", label: "Baixa" },
    { value: "NORMAL", label: "Normal" },
    { value: "ALTA", label: "Alta" },
    { value: "URGENTE", label: "Urgente" },
];

export function calcularExigeVeiculo(form, tiposProduto) {
    const tipoProdutoSelecionado = tiposProduto.find(t => t.id === form.tipoProdutoId);
    return !!tipoProdutoSelecionado?.exigeVeiculo;
}

/** Mesmas regras validadas no backend por EtapaCompraSolicitacaoHandler.validar / EtapaCompraCotacaoHandler.validarCamposCotacao. */
export function validarCamposCompras({ form, cotacao, rateio, exigeVeiculo }) {
    if (!form.filialId || !form.prioridade || !form.tipoProdutoId || !form.motivo) {
        return "Preencha todos os campos obrigatórios.";
    }
    if (form.necessitaCotacao === null) {
        return "Informe se a solicitação necessita de cotação.";
    }
    if (form.produtos.length === 0) {
        return "Informe pelo menos um produto.";
    }
    if (form.produtos.some(p => !p.produtoCatalogoId)) {
        return "Selecione o produto em todas as linhas.";
    }
    if (form.produtos.some(p => !p.detalhes || !p.detalhes.trim())) {
        return "Informe o detalhe de todos os produtos.";
    }
    if (exigeVeiculo && form.produtos.some(p => !p.placaVeiculo)) {
        return "É obrigatório informar o veículo para este tipo de produto.";
    }
    if (rateio.length === 0) {
        return "Informe ao menos uma conta BP no rateio orçamentário.";
    }
    if (form.necessitaCotacao === false) {
        const erroCotacao = validarCotacao(cotacao);
        if (erroCotacao) return erroCotacao;
    }
    return null;
}

export default function SolicitacaoComprasCampos({ form, setForm, cotacao, setCotacao, rateio, setRateio, filiais, tiposProduto }) {
    const exigeVeiculo = calcularExigeVeiculo(form, tiposProduto);

    // Ordena por Grupo (Departamento) para exibir os tipos de produto agrupados visualmente na
    // lista — o dropdown abaixo usa esse agrupamento via groupBy, com o grupo como cabeçalho não
    // selecionável. Tipos sem grupo cadastrado caem no cabeçalho "Outros".
    const tiposProdutoOrdenados = useMemo(() => {
        return [...tiposProduto].sort((a, b) => (a.grupo || "").localeCompare(b.grupo || "") || (a.nome || "").localeCompare(b.nome || ""));
    }, [tiposProduto]);

    // Correlaciona as linhas de cotação com os produtos pelo catálogo (id = produtoCatalogoId) —
    // a linha da solicitação (ProdutoSolicitacaoCompra) ainda não existe/tem id neste momento,
    // só depois de a solicitação ser criada/editada no backend (ver SolicitacaoCompraFluxo#aplicarDadosCotacao).
    const produtosParaCotacao = form.produtos
        .filter(p => p.produtoCatalogoId)
        .map(p => ({ id: p.produtoCatalogoId, nome: p.produtoCatalogoNome, quantidade: p.quantidade }));

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function setCotacaoCampo(campo, valor) {
        setCotacao(prev => ({ ...prev, [campo]: valor }));
    }

    function removerFornecedor(index) {
        setCotacao(prev => ({
            ...prev,
            fornecedores: prev.fornecedores.filter((_, i) => i !== index),
        }));
    }

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-8 gap-4">
                <div className="col-span-2 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Filial <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.filialId} onChange={e => set("filialId", e.value)} options={filiais} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                </div>
                <div className="col-span-2 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Prioridade <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.prioridade} onChange={e => set("prioridade", e.value)} options={PRIORIDADE_OPTIONS} placeholder="Selecione" />
                </div>
                <div className="col-span-4 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Tipo do produto <span className="text-destructive">*</span></label>
                    <CustomDropdown
                        value={form.tipoProdutoId}
                        onChange={e => set("tipoProdutoId", e.value)}
                        options={tiposProdutoOrdenados}
                        optionValue="id"
                        optionLabel="nome"
                        groupBy={t => t.grupo || "Outros"}
                        placeholder="Selecione"
                        filter
                    />
                </div>

            </div>

            <Input label="Motivo" required value={form.motivo} onChange={e => set("motivo", e.target.value)} />

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-2">Produtos <span className="text-destructive">*</span></label>
                <ProdutoEditor itens={form.produtos} onChange={itens => set("produtos", itens)} exigeVeiculo={exigeVeiculo} />
            </div>

            {form.necessitaCotacao === false && (
                <div className="flex flex-col gap-4 border border-border rounded-lg p-4 bg-muted/30">
                    <p className="text-xs text-muted-foreground">
                        Sem necessidade de cotação formal: informe abaixo o fornecedor e os valores negociados —
                        a solicitação seguirá direto para a Aprovação Corporativa, sem passar pelas etapas de Aprovação e Cotação.
                    </p>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Filial de Entrega <span className="text-destructive">*</span></label>
                        <CustomDropdown value={cotacao.filialEntregaId} onChange={e => setCotacaoCampo("filialEntregaId", e.value)} options={filiais} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-foreground/80 block mb-2">Fornecedores <span className="text-destructive">*</span></label>
                        <FornecedorCotacaoEditor
                            produtos={produtosParaCotacao}
                            fornecedores={cotacao.fornecedores}
                            onChange={fornecedores => setCotacaoCampo("fornecedores", fornecedores)}
                            onRemover={removerFornecedor}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Textarea
                            label="Observação"
                            value={cotacao.observacao}
                            onChange={e => setCotacaoCampo("observacao", e.target.value)}
                            rows={2}
                            placeholder="Alguma informação relevante sobre a cotação"
                        />

                        <InputMoney
                            label="Valor final da cotação"
                            required
                            locale="pt-BR"
                            value={cotacao.valorFinalCotacao}
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            mode="decimal"
                            onFocus={(e) => e.target.select()}
                            onChange={e => setCotacaoCampo("valorFinalCotacao", e.target.value)}
                            helpText="Valor final negociado com o fornecedor escolhido."
                        />
                    </div>
                </div>
            )}

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-2">Rateio Prévio <span className="text-destructive">*</span></label>
                <RateioOrcamentarioEditor itens={rateio} onChange={setRateio} filiais={filiais} />
            </div>
        </>
    );
}
